<template>
    <div>
        <rkm-row class="flex-nowrap">
            <rkm-col cols="3">
                <rkm-form-select
                    :options="form.tipos"
                    label="Tipo de certidão"
                    text-field="descricao"
                    v-model="model.documentos.certidao.tipoId"
                    value-field="id"
                    :validations="validations.tipo"
                    show-default-option
                />
            </rkm-col>

            <rkm-col class="flex-grow-0 text-nowrap">
                <rkm-form-radio-group
                    label="Modelo"
                    v-model="model.documentos.certidao.modeloId"
                    :options="form.modelos"
                    textField="nome"
                    valueField="id"
                    :disabled="!model.documentos.certidao.tipoId"
                />
            </rkm-col>

            <rkm-col class="flex-grow-1">
                <rkm-form-input
                    v-if="modeloNovo"
                    :validations="validations.numero.novo"
                    label="Número"
                    :key="'documento.novo.numero'"
                    v-mask="mask.numeroCertidao"
                    v-model="model.documentos.certidao.numero"
                    :disabled="!model.documentos.certidao.tipoId"
                />

                <rkm-form-input
                    v-else
                    :validations="validations.numero.antigo"
                    label="Termo"
                    :key="'documento.antigo.numero'"
                    v-model="model.documentos.certidao.numero"
                    :disabled="!model.documentos.certidao.tipoId"
                    uppercase
                />
            </rkm-col>
        </rkm-row>

        <template v-if="modeloAntigo">
            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input
                        :validations="validations.cartorioNome"
                        label="Nome do cartório"
                        v-model="model.documentos.certidao.cartorioNome"
                        :disabled="!model.documentos.certidao.numero"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.livro"
                        label="Livro"
                        v-model="model.documentos.certidao.livro"
                        :disabled="!model.documentos.certidao.numero"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        :validations="validations.folha"
                        label="Folha"
                        v-model="model.documentos.certidao.folha"
                        :disabled="!model.documentos.certidao.numero"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :validations="validations.dataEmissao"
                        label="Data de emissão"
                        v-model="model.documentos.certidao.dataEmissao"
                        :disabled="!model.documentos.certidao.numero"
                        :min-date="getDataNascimentoString()"
                        :max-date="dataAtual"
                        right
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-select
                        :options="form.estados"
                        label="UF"
                        text-field="uf"
                        v-model="form.estadoId"
                        value-field="id"
                        :validations="validations.estado"
                        :disabled="!model.documentos.certidao.numero"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :searchFields="localForm.autocompleteFields.municipio"
                        :repository="repositories.municipio"
                        :requestQueryStringParams="{ estado_id: form.estadoId }"
                        v-model="model.documentos.certidao.municipio"
                        :validations="validations.municipio"
                        label="Município"
                        :disabled="!form.estadoId"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :validations="validations.cartorioCodigo"
                        label="Código do cartório"
                        v-model="model.documentos.certidao.cartorioCodigo"
                        :disabled="!model.documentos.certidao.numero"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>
        </template>
    </div>
</template>

<script>
// Moment.
import moment from 'moment'

// Mixins.
import { repository, mask } from '@/mixins'

// Enums.
import modeloCertidao from '@/enums/cadastro/cidadao/modeloCertidao'

export default {
    props: {
        dataAtual: { type: String, required: true },
        form: { type: Object, required: true },
        model: { type: Object, required: true },
    },
    mixins: [ repository, mask ],
    data: () => ({
        repositories: {
            municipio: null,
        },
        localForm: {
            autocompleteFields: {
                municipio: [
                    { key: 'nome', searchable: true },
                ],
            },
        },
        enums: { modeloCertidao }
    }),
    computed: {
        modeloNovo() {

            return this.model.documentos.certidao.modeloId === this.enums.modeloCertidao.NOVO
        },
        modeloAntigo() {

            return this.model.documentos.certidao.modeloId === this.enums.modeloCertidao.ANTIGO
        },
        validations() {

            return {
                tipo: {},
                numero: {
                    antigo: {
                        required: !!this.model.documentos.certidao.tipoId,
                        min: 8,
                        max: 40
                    },
                    novo: {
                        required: !!this.model.documentos.certidao.tipoId,
                        min_max_without_mask: 32,
                    },
                },
                cartorioNome: {
                    required: !!this.model.documentos.certidao.numero,
                    max: 70
                },
                livro: {
                    required: !!this.model.documentos.certidao.numero,
                    max: 8
                },
                folha: {
                    required: !!this.model.documentos.certidao.numero,
                    min: 4,
                    max: 10
                },
                dataEmissao: {
                    required: !!this.model.documentos.certidao.numero,
                },
                estado: {
                    required: !!this.model.documentos.certidao.numero,
                },
                municipio: {
                    required: !!this.model.documentos.certidao.numero,
                },
                cartorioCodigo: {
                    required: !!this.model.documentos.certidao.numero,
                    min: 15,
                    max: 15
                }
            }
        }
    },
    watch: {
        'model.documentos.certidao.modeloId' : function () {

            this.model.documentos.certidao.numero = ''
            this.model.documentos.certidao.cartorioNome = ''
            this.model.documentos.certidao.livro = ''
            this.model.documentos.certidao.folha = ''
            this.model.documentos.certidao.dataEmissao = null
            this.form.estadoId = null
            this.model.documentos.certidao.cartorioCodigo = ''
        },
        'model.documentos.certidao.tipoId': function (id) {

            if (!id) {
                this.model.documentos.certidao.modeloId = this.enums.modeloCertidao.NOVO
            }
        },
        'model.documentos.certidao.numero': function (numero) {

            if (!numero && this.modeloAntigo) {

                this.model.documentos.certidao.cartorioNome = ''
                this.model.documentos.certidao.livro = ''
                this.model.documentos.certidao.folha = ''
                this.model.documentos.certidao.dataEmissao = null
                this.form.estadoId = null
                this.model.documentos.certidao.cartorioCodigo = ''
            }
        },
        'form.estadoId': function () {

            this.model.documentos.certidao.municipio = null
        },
    },
    created() {

        this.repositories.municipio = this.getConectaSusRepository('intranet.municipio')

        this.model.documentos.certidao.modeloId = this.enums.modeloCertidao.NOVO
    },
    methods: {
        getDataNascimentoString() {

            return this.model.dataNascimento
                ? moment(this.model.dataNascimento).format('DD/MM/YYYY')
                : null
        },
    },
}
</script>